/**
 * # as-tabs
 *
 * A tabbed container that shows one child panel at a time.
 * Each direct child becomes a tab panel. The tab label is taken from
 * the child's `tab-label` attribute.
 *
 * ## Attributes
 * - `active`: The index of the initially active tab (default: 0)
 * - `accordion`: When present, clicking the active tab toggles the panel
 *   open/closed. A caret on the active tab indicates the collapsed state.
 *
 * ## Events
 * - `as-tab-change`: Dispatched when the active tab changes
 *   - Detail: `{ index: number, label: string }`
 *
 * ## Usage
 * ```html
 * <as-tabs accordion>
 *   <div tab-label="Welcome">Welcome content</div>
 *   <div tab-label="News">News content</div>
 *   <div tab-label="Help">Help content</div>
 * </as-tabs>
 * ```
 */
import styleText from './as-tabs.css?raw';
import { sendEv } from '../../data/events.ts';

export class AsTabs extends HTMLElement {
  static readonly tag = 'as-tabs' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private _activeIndex = 0;
  private _collapsed = false;

  private get _isAccordion() {
    return this.hasAttribute('accordion');
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsTabs._sheet];

    this.shadowRoot!.innerHTML = /*html*/`
      <div class="tab-bar"></div>
      <div class="tab-panels">
        <slot></slot>
      </div>
    `;
  }

  connectedCallback() {
    this._activeIndex = parseInt(this.getAttribute('active') || '0', 10);

    const slot = this.shadowRoot!.querySelector('slot')!;
    slot.addEventListener('slotchange', () => this._buildTabs());

    this._buildTabs();
  }

  private _buildTabs() {
    const panels = this._getPanels();
    const bar = this.shadowRoot!.querySelector('.tab-bar')!;

    bar.innerHTML = '';
    panels.forEach((panel, i) => {
      const btn = document.createElement('button');
      btn.className = 'tab-button';

      const label = document.createElement('span');
      label.textContent = panel.getAttribute('tab-label') || `Tab ${i + 1}`;
      btn.appendChild(label);

      if (this._isAccordion) {
        const caret = document.createElement('span');
        caret.className = 'caret';
        btn.appendChild(caret);
      }

      btn.addEventListener('click', () => this._handleTabClick(i));
      bar.appendChild(btn);
    });

    this.setActive(this._activeIndex);
  }

  private _getPanels(): Element[] {
    const slot = this.shadowRoot!.querySelector('slot')!;
    return slot.assignedElements();
  }

  private _handleTabClick(index: number) {
    if (this._isAccordion && index === this._activeIndex) {
      this._collapsed = !this._collapsed;
      this._updateCollapsed();
      return;
    }
    this._collapsed = false;
    this.setActive(index);
  }

  private _updateCollapsed() {
    const panelsEl = this.shadowRoot!.querySelector('.tab-panels') as HTMLElement;
    if (this._collapsed) {
      panelsEl.classList.add('collapsed');
      this.setAttribute('collapsed', '');
    } else {
      panelsEl.classList.remove('collapsed');
      this.removeAttribute('collapsed');
    }

    // Update caret direction on active button
    const buttons = this.shadowRoot!.querySelectorAll('.tab-button');
    buttons.forEach((btn, i) => {
      if (i === this._activeIndex && this._collapsed) {
        btn.classList.add('collapsed');
      } else {
        btn.classList.remove('collapsed');
      }
    });
  }

  setActive(index: number) {
    const panels = this._getPanels();
    if (index < 0 || index >= panels.length) return;

    this._activeIndex = index;

    panels.forEach((panel, i) => {
      if (i === index) {
        panel.setAttribute('active', '');
      } else {
        panel.removeAttribute('active');
      }
    });

    const buttons = this.shadowRoot!.querySelectorAll('.tab-button');
    buttons.forEach((btn, i) => {
      if (i === index) {
        btn.setAttribute('active', '');
      } else {
        btn.removeAttribute('active');
        btn.classList.remove('collapsed');
      }
    });

    this._updateCollapsed();

    sendEv(this, 'as-tab-change', {
      detail: { index, label: panels[index]?.getAttribute('tab-label') || '' }
    });
  }
}

if (!customElements.get(AsTabs.tag)) {
  customElements.define(AsTabs.tag, AsTabs);
}
